/**
 * Research Window Presets — shared between Backtest and Walk-Forward.
 *
 * These are operator-curated BTC market-regime windows used for
 * repeatable research on the SAME market context across strategy
 * iterations. This file is the single source of truth.
 *
 * Hard contract:
 *   - All presets are BTCUSDT
 *   - All times are UTC midnight on the given calendar date
 *   - `single` presets carry one start/end pair
 *   - `composite` presets carry MULTIPLE non-contiguous sub-windows.
 *     The Backtest composite runner executes each sub-window
 *     INDEPENDENTLY and aggregates — it MUST NOT be flattened into
 *     a single contiguous range.
 *
 * NO strategy logic, NO backtest math, NO live trading touched.
 */
const UTC_START = "T00:00:00";
const UTC_END = "T23:59:00";

// `datetime-local` form fields use the local naive ISO format
// (`YYYY-MM-DDTHH:mm`) — same shape as the existing Quick-Range
// buttons produce via `isoToInput(...)`. Inputs stay in user-local
// formatting; the submit handler converts to UTC ISO with `.toISOString()`.
const _start = (d) => `${d}${UTC_START}`;
const _end   = (d) => `${d}${UTC_END}`;

export const RESEARCH_PRESETS = [
  // Sentinel: lets the user explicitly opt back into manual editing.
  {
    id: "custom",
    label: "Custom Date Range",
    regime_type: "custom",
    type: "single",
    start_iso: null,
    end_iso: null,
  },

  // ---- Single-window regimes -----------------------------------------
  {
    id: "uptrend_1",
    label: "Uptrend 1",
    regime_type: "uptrend",
    type: "single",
    start_iso: _start("2026-03-29"),
    end_iso:   _end("2026-04-29"),
  },
  {
    id: "uptrend_2",
    label: "Uptrend 2",
    regime_type: "uptrend",
    type: "single",
    start_iso: _start("2025-12-16"),
    end_iso:   _end("2026-01-15"),
  },
  {
    id: "strong_uptrend_1",
    label: "Strong Uptrend 1",
    regime_type: "strong_uptrend",
    type: "single",
    start_iso: _start("2023-10-10"),
    end_iso:   _end("2023-11-12"),
  },
  {
    id: "strong_uptrend_2",
    label: "Strong Uptrend 2",
    regime_type: "strong_uptrend",
    type: "single",
    start_iso: _start("2023-01-01"),
    end_iso:   _end("2023-02-02"),
  },
  {
    id: "chop_1",
    label: "Chop 1",
    regime_type: "chop",
    type: "single",
    start_iso: _start("2024-11-22"),
    end_iso:   _end("2024-12-22"),
  },
  {
    id: "chop_2",
    label: "Chop 2",
    regime_type: "chop",
    type: "single",
    start_iso: _start("2026-02-11"),
    end_iso:   _end("2026-03-13"),
  },
  {
    id: "strong_downtrend_1",
    label: "Strong Downtrend 1",
    regime_type: "strong_downtrend",
    type: "single",
    start_iso: _start("2026-01-05"),
    end_iso:   _end("2026-02-06"),
  },
  {
    id: "downtrend_1",
    label: "Downtrend 1",
    regime_type: "downtrend",
    type: "single",
    start_iso: _start("2025-10-06"),
    end_iso:   _end("2025-11-05"),
  },
  {
    id: "downtrend_2",
    label: "Downtrend 2",
    regime_type: "downtrend",
    type: "single",
    start_iso: _start("2025-01-20"),
    end_iso:   _end("2025-02-28"),
  },
  {
    id: "strong_downtrend_2",
    label: "Strong Downtrend 2",
    regime_type: "strong_downtrend",
    type: "single",
    start_iso: _start("2025-10-27"),
    end_iso:   _end("2025-11-21"),
  },

  // ---- Composite (non-contiguous) -----------------------------------
  // MUST NOT be flattened into 2025-02-04 → 2025-05-20: that would
  // sweep across non-consolidation regimes and corrupt the analytic
  // signal. The composite runner executes each window independently.
  {
    id: "consolidation_composite",
    label: "Consolidation Composite",
    regime_type: "consolidation",
    type: "composite",
    windows: [
      { start_iso: _start("2025-02-04"), end_iso: _end("2025-02-23") },
      { start_iso: _start("2025-05-08"), end_iso: _end("2025-05-20") },
      { start_iso: _start("2025-04-23"), end_iso: _end("2025-05-07") },
    ],
  },
];

export function findPreset(id) {
  if (!id) return null;
  return RESEARCH_PRESETS.find((p) => p.id === id) || null;
}

export function isCompositePreset(id) {
  const p = findPreset(id);
  return !!(p && p.type === "composite");
}

/**
 * Returns the form-shape (start_iso/end_iso) for a single preset,
 * or null when the preset is composite / custom / unknown. The
 * caller is expected to handle composite/custom branches explicitly.
 */
export function singlePresetDates(id) {
  const p = findPreset(id);
  if (!p) return null;
  if (p.type !== "single") return null;
  if (!p.start_iso || !p.end_iso) return null;
  return { start_iso: p.start_iso, end_iso: p.end_iso };
}
